import { WIX_ECOM } from '@wix/app-definition-ids';

import { APP_WIDGET_LOGIN_MENU_ID } from '../../../../constants';
import { Experiment } from '../../../../constants/experiment';
import type { ContextProps } from '../../../../types/controller';
import { addToCart, enrichWithStockStatus, fetchOrderItems, type OrderItem } from '../services/buy-again';
import { PLACEHOLDER_ORDER_ITEMS } from './buy-again.editor-placeholder';

const MAX_BUY_AGAIN_ITEMS = 3;
const STORES_PRODUCT_PAGE_SECTION_ID = 'product_page';

const isStoresInstalled = (wixCodeApi: ContextProps['wixCodeApi']): boolean =>
  !!wixCodeApi.site.isAppSectionInstalled({ sectionId: STORES_PRODUCT_PAGE_SECTION_ID, appDefinitionId: WIX_ECOM });

interface BuyAgainAddToCartEventPayload {
  type: 'onBuyAgainAddToCart';
  catalogItemId: string;
  catalogOptions?: Record<string, any>;
}

enum CartClickAction {
  SideCart = 'sideCart',
  CartPage = 'cartPage',
}

interface LoginBarApi {
  readonly buyAgainSettings?: { cartClickAction?: CartClickAction };
  buyAgainOrderItems?: OrderItem[];
  onBuyAgainAddToCart?: (
    handler: (event: BuyAgainAddToCartEventPayload) => void,
  ) => void;
  confirmBuyAgainItemAdded?: (
    catalogItemId: string,
    error?: unknown,
  ) => void;
}

interface EcomCartApi {
  reloadCart?: () => Promise<void>;
  openSideCart?: () => void;
}

interface EcomNavigateApi {
  toCart?: () => Promise<void>;
}

interface EcomPublicApi {
  cart?: EcomCartApi;
  navigate?: EcomNavigateApi;
}

const registeredLoginBars = new WeakSet<LoginBarApi>();
let knownCatalogIds = new Set<string>();
let orderItemsPromise: Promise<OrderItem[]> | null = null;
let ecomApiPromise: Promise<EcomPublicApi | null> | null = null;
let loginListenerRegistered = false;
let lastContextProps: ContextProps | null = null;

const buildReportError =
  (contextProps: ContextProps) =>
  (error: unknown): void => {
    const { errorHandler, panoramaClient } = contextProps.flowAPI;
    const { httpError } = errorHandler.getResolvedError(error);
    panoramaClient?.errorMonitor().reportError((httpError || error) as Error);
  };

const registerBuyAgainHandler = async (
  contextProps: ContextProps,
  reportError: (error: unknown) => void,
): Promise<void> => {
  const { wixCodeApi, flowAPI } = contextProps;
  const { httpClient } = flowAPI;

  const loginBars = contextProps.$w(
    APP_WIDGET_LOGIN_MENU_ID,
  ) as LoginBarApi[];
  if (loginBars.length === 0) {
    return;
  }

  if (!ecomApiPromise) {
    ecomApiPromise = wixCodeApi.site
      .getPublicAPI(WIX_ECOM)
      .then((api) => {
        const ecomApi = api as EcomPublicApi | null;
        return ecomApi?.cart && ecomApi.navigate ? ecomApi : null;
      })
      .catch(() => null);
  }
  const ecomApi = await ecomApiPromise;
  if (!ecomApi?.cart || !ecomApi.navigate) {
    ecomApiPromise = null;
    return;
  }
  const { cart, navigate } = ecomApi;

  if (!orderItemsPromise) {
    orderItemsPromise = fetchOrderItems(httpClient, MAX_BUY_AGAIN_ITEMS)
      .then((items) => enrichWithStockStatus(httpClient, items));
  }
  let orderItems: OrderItem[];
  try {
    orderItems = await orderItemsPromise;
  } catch (error) {
    orderItemsPromise = null;
    reportError(error);
    return;
  }

  if (orderItems.length === 0) {
    return;
  }

  knownCatalogIds = new Set(orderItems.map((item) => item.catalogItemId));

  for (const loginBar of loginBars) {
    loginBar.buyAgainOrderItems = orderItems;

    if (registeredLoginBars.has(loginBar)) {
      continue;
    }
    registeredLoginBars.add(loginBar);

    loginBar.onBuyAgainAddToCart?.(async ({ catalogItemId, catalogOptions }) => {
      if (!knownCatalogIds.has(catalogItemId)) {
        return;
      }
      try {
        await addToCart(httpClient, catalogItemId, catalogOptions);
        await cart.reloadCart?.();
        if (loginBar.buyAgainSettings?.cartClickAction === CartClickAction.SideCart) {
          cart.openSideCart?.();
        } else {
          await navigate.toCart?.();
        }
        loginBar.confirmBuyAgainItemAdded?.(catalogItemId);
      } catch (error) {
        reportError(error);
        loginBar.confirmBuyAgainItemAdded?.(catalogItemId, error);
      }
    });
  }
};

const injectPlaceholderBuyAgainItems = (contextProps: ContextProps): void => {
  const loginBars = contextProps.$w(
    APP_WIDGET_LOGIN_MENU_ID,
  ) as LoginBarApi[];
  for (const loginBar of loginBars) {
    if (registeredLoginBars.has(loginBar)) {
      continue;
    }
    registeredLoginBars.add(loginBar);
    loginBar.buyAgainOrderItems = PLACEHOLDER_ORDER_ITEMS;
    loginBar.onBuyAgainAddToCart?.(({ catalogItemId }) => {
      loginBar.confirmBuyAgainItemAdded?.(catalogItemId);
    });
  }
};

export const addBuyAgainHandler = async (
  contextProps: ContextProps,
): Promise<void> => {
  const { wixCodeApi, flowAPI } = contextProps;
  const { environment, experiments } = flowAPI;

  if (!experiments.enabled(Experiment.LoginSocialBarBuyAgain)) {
    return;
  }

  if (!isStoresInstalled(wixCodeApi)) {
    return;
  }

  if (!environment.isViewer) {
    injectPlaceholderBuyAgainItems(contextProps);
    return;
  }

  lastContextProps = contextProps;

  if (!loginListenerRegistered) {
    loginListenerRegistered = true;
    wixCodeApi.user.onLogin(async () => {
      const ctx = lastContextProps;
      if (!ctx) {
        return;
      }
      orderItemsPromise = null;
      await registerBuyAgainHandler(ctx, buildReportError(ctx));
    });
  }

  if (wixCodeApi.user.currentUser.loggedIn) {
    await registerBuyAgainHandler(contextProps, buildReportError(contextProps));
  }
};
